import Cookies from "js-cookie";
import API_BASE_URL from "./config";


/*
|--------------------------------------------------------------------------
| CSRF BASE URL
|--------------------------------------------------------------------------
|
| Example:
| API_BASE_URL = http://localhost:8000/api/user
|
| Then CSRF URL becomes:
| http://localhost:8000/sanctum/csrf-cookie
|
|--------------------------------------------------------------------------
*/

const getBackendBaseURL = () => {

    return API_BASE_URL
        .replace(/\/api\/user\/?$/, "")
        .replace(/\/api\/?$/, "")
        .replace(/\/+$/, "");

};


/*
|--------------------------------------------------------------------------
| Get CSRF Cookie
|--------------------------------------------------------------------------
*/

export const getCsrfCookie = async () => {

    const csrfURL =
        `${getBackendBaseURL()}/sanctum/csrf-cookie`;


    console.log(
        "🔐 Getting CSRF Cookie:",
        csrfURL
    );


    const response = await fetch(
        csrfURL,
        {
            method: "GET",

            headers: {

                "Accept":
                    "application/json"

            },

            credentials:
                "include"
        }
    );


    if (!response.ok) {

        console.error(
            "❌ CSRF Cookie Request Failed:",
            response.status
        );

    }


    return response;

};


/*
|--------------------------------------------------------------------------
| Get XSRF Token
|--------------------------------------------------------------------------
*/

const getXsrfToken = () => {

    const name =
        "XSRF-TOKEN=";


    const cookies =
        document.cookie.split(";");


    for (
        let cookie of cookies
    ) {

        cookie =
            cookie.trim();


        if (
            cookie.startsWith(name)
        ) {

            return decodeURIComponent(
                cookie.substring(
                    name.length
                )
            );

        }

    }


    return null;

};


/*
|--------------------------------------------------------------------------
| Get Common Headers With CSRF
|--------------------------------------------------------------------------
*/

const getCsrfHeaders = () => {

    const xsrfToken =
        getXsrfToken();


    const headers = {

        "Accept":
            "application/json",

        "Content-Type":
            "application/json"

    };


    if (xsrfToken) {

        headers[
            "X-XSRF-TOKEN"
        ] = xsrfToken;

    }


    return headers;

};


/*
|--------------------------------------------------------------------------
| Save User Information In Cookies
|--------------------------------------------------------------------------
*/

const saveUserCookies = (
    user
) => {

    if (!user) {

        return;

    }


    /*
    |--------------------------------------------------------------------------
    | User ID
    |--------------------------------------------------------------------------
    */

    if (
        user.id !== undefined &&
        user.id !== null
    ) {

        Cookies.set(
            "user_id",
            String(user.id),
            {
                expires: 7,
                sameSite: "Lax"
            }
        );

    }


    /*
    |--------------------------------------------------------------------------
    | Custom User ID
    |--------------------------------------------------------------------------
    */

    if (
        user.user_id !== undefined &&
        user.user_id !== null
    ) {

        Cookies.set(
            "user_user_id",
            String(user.user_id),
            {
                expires: 7,
                sameSite: "Lax"
            }
        );

    }


    /*
    |--------------------------------------------------------------------------
    | Full Name
    |--------------------------------------------------------------------------
    */

    if (user.fullname) {

        Cookies.set(
            "username",
            user.fullname,
            {
                expires: 7,
                sameSite: "Lax"
            }
        );

    }


    /*
    |--------------------------------------------------------------------------
    | Email
    |--------------------------------------------------------------------------
    */

    if (user.email) {

        Cookies.set(
            "user_email",
            user.email,
            {
                expires: 7,
                sameSite: "Lax"
            }
        );

    }


    /*
    |--------------------------------------------------------------------------
    | Mobile
    |--------------------------------------------------------------------------
    */

    if (user.mobile) {

        Cookies.set(
            "user_mobile",
            user.mobile,
            {
                expires: 7,
                sameSite: "Lax"
            }
        );

    }


    /*
    |--------------------------------------------------------------------------
    | Address
    |--------------------------------------------------------------------------
    */

    if (user.address) {

        Cookies.set(
            "user_address",
            user.address,
            {
                expires: 7,
                sameSite: "Lax"
            }
        );

    }


    /*
    |--------------------------------------------------------------------------
    | City
    |--------------------------------------------------------------------------
    */

    if (user.city) {

        Cookies.set(
            "user_city",
            user.city,
            {
                expires: 7,
                sameSite: "Lax"
            }
        );

    }


    /*
    |--------------------------------------------------------------------------
    | Batch Year
    |--------------------------------------------------------------------------
    */

    if (user.batch_year) {

        Cookies.set(
            "user_batch_year",
            user.batch_year,
            {
                expires: 7,
                sameSite: "Lax"
            }
        );

    }


    /*
    |--------------------------------------------------------------------------
    | College
    |--------------------------------------------------------------------------
    */

    if (user.college) {

        Cookies.set(
            "user_college",
            user.college,
            {
                expires: 7,
                sameSite: "Lax"
            }
        );

    }


    /*
    |--------------------------------------------------------------------------
    | Register Date
    |--------------------------------------------------------------------------
    */

    if (user.register_date) {

        Cookies.set(
            "user_register_date",
            user.register_date,
            {
                expires: 7,
                sameSite: "Lax"
            }
        );

    }


    console.log(
        "✅ USER DATA SAVED IN COOKIES"
    );

};


/*
|--------------------------------------------------------------------------
| Remove User Cookies
|--------------------------------------------------------------------------
*/

export const removeUserCookies = () => {

    Cookies.remove(
        "user_id"
    );

    Cookies.remove(
        "user_user_id"
    );

    Cookies.remove(
        "username"
    );

    Cookies.remove(
        "user_email"
    );

    Cookies.remove(
        "user_mobile"
    );

    Cookies.remove(
        "user_address"
    );

    Cookies.remove(
        "user_city"
    );

    Cookies.remove(
        "user_batch_year"
    );

    Cookies.remove(
        "user_college"
    );

    Cookies.remove(
        "user_register_date"
    );

};


/*
|--------------------------------------------------------------------------
| User Login
|--------------------------------------------------------------------------
*/

export const userLogin = async (
    email,
    password
) => {

    await getCsrfCookie();


    const response =
        await fetch(
            `${API_BASE_URL}/user/login`,
            {

                method: "POST",

                headers:
                    getCsrfHeaders(),

                credentials:
                    "include",

                body:
                    JSON.stringify({

                        email:
                            email,

                        password:
                            password

                    })

            }
        );


    const responseClone =
        response.clone();


    try {

        const data =
            await responseClone.json();


        if (
            response.ok &&
            data.success &&
            data.user
        ) {

            saveUserCookies(
                data.user
            );

        }

    } catch (error) {

        console.error(
            "Unable to read login response:",
            error
        );

    }


    return response;

};


/*
|--------------------------------------------------------------------------
| User Signup
|--------------------------------------------------------------------------
*/

export const userSignup = async (
    formData
) => {

    await getCsrfCookie();


    const response =
        await fetch(
            `${API_BASE_URL}/user/signup`,
            {

                method: "POST",

                headers:
                    getCsrfHeaders(),

                credentials:
                    "include",

                body:
                    JSON.stringify({

                        

                        fullname:
                            formData.full_name,

                        email:
                            formData.email,

                        mobile:
                            formData.mobile,

                        college:
                            formData.college,

                        city:
                            formData.city,

                        address:
                            formData.address,

                        batch_year:
                            formData.batch_year,

                        password:
                            formData.password,

                        password_confirmation:
                            formData.confirm_password

                    })

            }
        );


    const responseClone =
        response.clone();


    try {

        const data =
            await responseClone.json();


        if (
            response.ok &&
            data.success &&
            data.user
        ) {

            saveUserCookies(
                data.user
            );

        }

    } catch (error) {

        console.error(
            "Unable to read signup response:",
            error
        );

    }


    return response;

};


/*
|--------------------------------------------------------------------------
| User Me
|--------------------------------------------------------------------------
*/

export const userMe = async () => {

    return fetch(
        `${API_BASE_URL}/user/me`,
        {

            method: "GET",

            headers: {

                "Accept":
                    "application/json"

            },

            credentials:
                "include"

        }
    );

};


/*
|--------------------------------------------------------------------------
| Get Free MCQ Papers
|--------------------------------------------------------------------------
*/

export const getFreeMCQ = async () => {

    try {

        return await fetch(
            `${API_BASE_URL}/free-mcq`,
            {

                method: "GET",

                headers: {

                    "Accept":
                        "application/json"

                },

                credentials:
                    "include"

            }
        );

    } catch (error) {

        console.error(
            "getFreeMCQ Fetch Error:",
            error
        );

        throw error;

    }

};


/*
|--------------------------------------------------------------------------
| Get Free MCQ Questions
|--------------------------------------------------------------------------
*/

export const getFreeMCQQuestions = async (
    paperId
) => {

    try {

        return await fetch(
            `${API_BASE_URL}/free-mcq/${encodeURIComponent(paperId)}/questions`,
            {

                method: "GET",

                headers: {

                    "Accept":
                        "application/json"

                },

                credentials:
                    "include"

            }
        );

    } catch (error) {

        console.error(
            "getFreeMCQQuestions Fetch Error:",
            error
        );

        throw error;

    }

};


/*
|--------------------------------------------------------------------------
| Submit Free MCQ
|--------------------------------------------------------------------------
*/

export const submitFreeMCQ = async (
    paperId,
    answers,
    examStartTime
) => {

    try {

        await getCsrfCookie();


        const response =
            await fetch(
                `${API_BASE_URL}/free-mcq/${encodeURIComponent(paperId)}/submit`,
                {

                    method: "POST",

                    headers:
                        getCsrfHeaders(),

                    credentials:
                        "include",

                    body:
                        JSON.stringify({

                            answers:
                                answers,

                            exam_start_time:
                                examStartTime

                        })

                }
            );


        return response;

    } catch (error) {

        console.error(
            "submitFreeMCQ Fetch Error:",
            error
        );

        throw error;

    }

};


/*
|--------------------------------------------------------------------------
| Get Premium MCQ Papers
|--------------------------------------------------------------------------
*/

export const getPremiumMCQ = async () => {

    try {

        return await fetch(
            `${API_BASE_URL}/premium-mcq`,
            {

                method: "GET",

                headers: {

                    "Accept":
                        "application/json"

                },

                credentials:
                    "include"

            }
        );

    } catch (error) {

        console.error(
            "getPremiumMCQ Fetch Error:",
            error
        );

        throw error;

    }

};


/*
|--------------------------------------------------------------------------
| Get Premium MCQ Subjects
|--------------------------------------------------------------------------
*/

export const getPremiumSubjects = async () => {

    try {

        return await fetch(
            `${API_BASE_URL}/premium-mcq/subjects`,
            {

                method: "GET",

                headers: {

                    "Accept":
                        "application/json"

                },

                credentials:
                    "include"

            }
        );

    } catch (error) {

        console.error(
            "getPremiumSubjects Fetch Error:",
            error
        );

        throw error;

    }

};


/*
|--------------------------------------------------------------------------
| Get Premium MCQ Papers By Subject
|--------------------------------------------------------------------------
*/

export const getPremiumPapers = async (
    subject
) => {

    try {

        return await fetch(
            `${API_BASE_URL}/premium-mcq/papers/${encodeURIComponent(subject)}`,
            {

                method: "GET",

                headers: {

                    "Accept":
                        "application/json"

                },

                credentials:
                    "include"

            }
        );

    } catch (error) {

        console.error(
            "getPremiumPapers Fetch Error:",
            error
        );

        throw error;

    }

};


/*
|--------------------------------------------------------------------------
| Start Premium MCQ
|--------------------------------------------------------------------------
*/

export const startPremiumMCQ = async (
    paperId
) => {

    try {

        return await fetch(
            `${API_BASE_URL}/premium-mcq/start/${encodeURIComponent(paperId)}`,
            {

                method: "GET",

                headers: {

                    "Accept":
                        "application/json"

                },

                credentials:
                    "include"

            }
        );

    } catch (error) {

        console.error(
            "startPremiumMCQ Fetch Error:",
            error
        );

        throw error;

    }

};


/*
|--------------------------------------------------------------------------
| Submit Premium MCQ
|--------------------------------------------------------------------------
*/

export const submitPremiumMCQ = async (
    paperId,
    answers,
    examStartTime
) => {

    try {

        /*
        |--------------------------------------------------------------------------
        | Get CSRF Cookie
        |--------------------------------------------------------------------------
        */

        const csrfResponse =
            await getCsrfCookie();

        if (!csrfResponse.ok) {

            console.error(
                "❌ CSRF Cookie Failed:",
                csrfResponse.status
            );

            throw new Error(
                "Unable to initialize CSRF protection."
            );
        }


        /*
        |--------------------------------------------------------------------------
        | Check XSRF Token
        |--------------------------------------------------------------------------
        */

        const xsrfToken =
            getXsrfToken();

        console.log(
            "🔐 XSRF Token:",
            xsrfToken
                ? "FOUND"
                : "NOT FOUND"
        );


        if (!xsrfToken) {

            console.error(
                "❌ XSRF-TOKEN cookie was not found."
            );

            throw new Error(
                "CSRF cookie was not received."
            );
        }


        /*
        |--------------------------------------------------------------------------
        | Submit Premium MCQ
        |--------------------------------------------------------------------------
        */

        const submitURL =
            `${API_BASE_URL}/premium-mcq/${encodeURIComponent(
                paperId
            )}/submit`;


        console.log(
            "📤 Premium Submit URL:",
            submitURL
        );


        const response =
            await fetch(
                submitURL,
                {
                    method: "POST",

                    credentials: "include",

                    headers: {
                        ...getCsrfHeaders(),

                        "X-XSRF-TOKEN":
                            xsrfToken
                    },

                    body:
                        JSON.stringify({

                            answers:
                                answers,

                            exam_start_time:
                                examStartTime

                        })
                }
            );


        return response;


    } catch (error) {

        console.error(
            "❌ submitPremiumMCQ Error:",
            error
        );

        throw error;
    }
};


/*
|--------------------------------------------------------------------------
| Get Subscription Plans
|--------------------------------------------------------------------------
*/

export const getSubscriptions = async () => {

    try {

        return await fetch(
            `${API_BASE_URL}/subscriptions`,
            {

                method: "GET",

                headers: {

                    "Accept":
                        "application/json"

                },

                credentials:
                    "include"

            }
        );

    } catch (error) {

        console.error(
            "getSubscriptions Fetch Error:",
            error
        );

        throw error;

    }

};



// start prime paper category 

export async function getPremiumPaperCategories() {
    const response = await fetch(
        `${API_BASE_URL}/premium-paper-categories`,
        {
            method: "GET",
            credentials: "include",
            headers: {
                Accept: "application/json",
            },
        }
    );

    return response;
}


export async function getPrimePaperList(category) {

    const response = await fetch(
        `${API_BASE_URL}/primepaperlist/${encodeURIComponent(category)}`,
        {
            method: "GET",

            credentials: "include",

            headers: {
                Accept: "application/json",
            },
        }
    );

    return response;
}


export async function startPrimePaper(paperId) {
    const response = await fetch(
        `${API_BASE_URL}/premium-paper/start/${encodeURIComponent(paperId)}`,
        {
            method: "GET",
            credentials: "include",
            headers: {
                Accept: "application/json",
            },
        }
    );

    return response;
}



export async function submitPrimePaper(
    paperId,
    answers,
    examStartTime
) {
    // Get CSRF cookie first
    await fetch(`${API_BASE_URL}/sanctum/csrf-cookie`, {
        method: "GET",
        credentials: "include",
        headers: {
            Accept: "application/json",
        },
    });

    // Read XSRF token from cookie
    const xsrfToken = document.cookie
        .split("; ")
        .find((row) => row.startsWith("XSRF-TOKEN="))
        ?.split("=")[1];

    const response = await fetch(
        `${API_BASE_URL}/premium-paper/${encodeURIComponent(paperId)}/submit`,
        {
            method: "POST",
            credentials: "include",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                "X-XSRF-TOKEN": xsrfToken
                    ? decodeURIComponent(xsrfToken)
                    : "",
            },
            body: JSON.stringify({
                answers,
                exam_start_time: examStartTime,
            }),
        }
    );

    return response;
}




/*
|--------------------------------------------------------------------------
| User Logout
|--------------------------------------------------------------------------
*/

export const userLogout = async () => {

    await getCsrfCookie();


    const response =
        await fetch(
            `${API_BASE_URL}/user/logout`,
            {

                method: "POST",

                headers:
                    getCsrfHeaders(),

                credentials:
                    "include"

            }
        );


    if (
        response.ok
    ) {

        removeUserCookies();

    }


    return response;

};


// forgot password 
/*
|--------------------------------------------------------------------------
| Send Forgot Password OTP
|--------------------------------------------------------------------------
*/

export const userForgotPasswordSendOtp = async (email) => {

    return fetch(
        `${API_BASE_URL}/user/forgot-password/send-otp`,
        {
            method: "POST",

            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json"
            },

            credentials: "include",

            body: JSON.stringify({
                email: email
            })
        }
    );
};


/*
|--------------------------------------------------------------------------
| Verify Forgot Password OTP
|--------------------------------------------------------------------------
*/

export const userForgotPasswordVerifyOtp = async (
    email,
    otp
) => {

    return fetch(
        `${API_BASE_URL}/user/forgot-password/verify-otp`,
        {
            method: "POST",

            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json"
            },

            credentials: "include",

            body: JSON.stringify({
                email: email,
                otp: otp
            })
        }
    );
};


/*
|--------------------------------------------------------------------------
| Reset Password
|--------------------------------------------------------------------------
*/

export const userForgotPasswordReset = async (
    email,
    password,
    passwordConfirmation
) => {

    return fetch(
        `${API_BASE_URL}/user/forgot-password/reset`,
        {
            method: "POST",

            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json"
            },

            credentials: "include",

            body: JSON.stringify({

                email: email,

                password: password,

                password_confirmation:
                    passwordConfirmation

            })
        }
    );
};